import React, { useState, useEffect, useContext } from 'react';
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { LanguageContext } from '../components/LanguageContext'; // Adjust the path as needed
import ah1 from '../images/ah1.webp';
import ah5 from '../images/ah8.jpeg';
import ah3 from '../images/ah3.webp';
import ah2 from '../images/ah2.webp';
import ah10 from '../images/ah10.png';
import ah4 from '../images/ah7.png';
import alogo from '../images/alogo.svg';

const NavLink = ({ href, children }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Link
      to={href}
      className="relative py-1 px-1 text-md font-medium group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span className={`relative z-10 transition-colors duration-300 ${
        isHovered ? 'text-blue-800' : 'text-black'
      }`}>
        {children}
      </span>
      <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-blue-800 transform origin-left transition-transform duration-300 ease-out ${
        isHovered ? 'scale-x-100' : 'scale-x-0'
      }`} />
    </Link>
  );
};

const MoreDropdown = ({ items, language, closeMenu }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative">
      <button 
        className="flex items-center py-1 px-1 text-md font-medium"
        onClick={() => setIsOpen(!isOpen)}
        onBlur={() => setTimeout(() => setIsOpen(false), 100)}
      >
        <span className="mr-1">{language === 'en' ? 'More' : 'مزید'}</span>
        <FontAwesomeIcon icon={faChevronDown} size="xs" />
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
          {items.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="block px-4 py-2 hover:bg-blue-50 hover:text-blue-800"
              onClick={() => {
                setIsOpen(false);
                if (closeMenu) closeMenu();
              }}
            >
              {language === 'en' ? item.name_EN : item.name_UR}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Navbar = ({ logo }) => {
  const { language, toggleLanguage } = useContext(LanguageContext);
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    // No spacer logic needed for static navbar
    return () => {};
  }, [isOpen]);
  
  // Leaders data
  const leaders = [
    {
      id: 1,
      name: 'Shri Narendra Modi',
      designation: 'Hon\'ble Prime Minister',
      image: ah10
    },
    {
      id: 2,
      name: 'Janab Chandrababu Naidu',
      designation: 'Hon\'ble Chief Minister of AP',
      image: ah1
    },
    {
      id: 3,
      name: 'Janab K. Pawan Kalyan',
      designation: 'Hon\'ble Deputy Chief Minister of AP',
      image: ah2
    },
    {
      id: 4,
      name: 'Janab Nara Lokesh',
      designation: 'Hon\'ble Minister for Human Resources Development',
      image: ah5
    },
    {
      id: 5,
      name: 'Janab N. M. D. Farooq',
      designation: 'Hon\'ble Minister for Law & Justice and Minorities Welfare',
      image: ah3
    },
    {
      id: 6,
      name: 'Janab Mohammed Farooq Shubli',
      designation: 'Hon\'ble Chairman for AP State Urdu Academy',
      image: ah4
    }
  ];

  // Adjust navItems to support both languages
  const navItems = [
    { name_EN: 'Home', name_UR: 'ہوم', path: '/' },
    { name_EN: 'About Us', name_UR: 'ہمارے بارے میں', path: '/about-us' },
    { name_EN: 'Academy Schemes', name_UR: 'اکیڈمی کے منصوبے', path: '/academy-schemes' },
    { name_EN: 'News & Events', name_UR: 'خبریں اور واقعات', path: '/news-events' },
    { name_EN: 'Magazines', name_UR: 'رسالے', path: '/magazines' },
    { name_EN: 'Contact Us', name_UR: 'ہم سے رابطہ کریں', path: '/contact-us' },
    { name_EN: 'Our Centers', name_UR: 'ہمارے مراکز', path: '/our-centers' },
    { name_EN: 'Students Zone', name_UR: 'طلباء کا زون', path: '/students-zone' },
    { name_EN: 'Applications', name_UR: 'درخواستیں', path: '/form-applications' },
  ];

  // Primary visible items for medium screens (adjust how many items show)
  const primaryNavItems = navItems.slice(0, 3);
  const secondaryNavItems = navItems.slice(3);

  return (
    <>
      {/* Navbar */}
      <header className="mb-0">
        <nav className="bg-white border-b border-gray-200 w-full shadow-sm">
          <div className="mx-auto px-4">
          {/* Mobile Menu Toggle */}
          <div className="absolute top-4 right-4 md:hidden">
            <button 
              className="text-gray-800 focus:outline-none p-2" 
              onClick={toggleMenu}
            >
              <FontAwesomeIcon icon={faBars} size="lg" />
            </button>
          </div>

          {/* Logo and Title Row */}
          <div className="flex flex-col md:flex-row justify-center items-center py-4 gap-4 md:gap-6 px-4">
            {/* Logo - Always visible */}
            <div className="flex-shrink-0">
              <img src={alogo} alt="Logo" className="h-12 md:h-24 lg:h-28 w-auto object-contain" />
            </div>
            
            {/* Title - Always visible */}
            <div className="flex-shrink-0 text-center">
              <div className="flex flex-col items-center">
                <span className="text-xl md:text-3xl lg:text-4xl font-bold text-blue-800 transition-colors duration-300 mb-1 md:mb-2">
                  {language === 'en' ? "AP State Urdu Academy" : "اے پی اسٹیٹ اردو اکیڈمی"}
                </span>
                <h1 className="text-sm md:text-xl lg:text-2xl font-bold text-blue-800 transition-colors duration-300">
                  {language === 'en' ? "Preserving and Promoting Urdu Language" : "اردو زبان کی حفاظت اور فروغ"}
                </h1>
              </div>
            </div>
            
            {/* Logo (repeated) - Always visible */}
            <div className="flex-shrink-0">
              <img src={logo} alt="Logo" className="h-12 md:h-24 lg:h-28 w-auto object-contain" />
            </div>
          </div>

          {/* Leaders Row */}
          <div className="hidden md:flex justify-center gap-8 py-4">
            {leaders.map((leader, index) => (
              <div key={leader.id} className="flex flex-col items-center text-center">
                <div className={`${index === 0 ? 'w-28 h-28 md:w-32 md:h-32' : 'w-20 h-20 md:w-24 md:h-24'} relative mb-2`}>
                  <img
                    src={leader.image}
                    alt={leader.name}
                    className="w-full h-full rounded-full object-fit border-3 border-blue-200 shadow-md"
                  />
                </div>
                <div className={`${index === 0 ? 'max-w-[180px]' : 'max-w-[150px]'}`}>
                  <p className={`${index === 0 ? 'text-base' : 'text-sm'} font-semibold text-gray-900 leading-tight`}>{leader.name}</p>
                  <p className={`${index === 0 ? 'text-base' : 'text-sm'} text-blue-800 leading-tight`}>{leader.designation}</p>
                </div>
              </div>
            ))}
            
            {/* Mobile Menu Toggle */}
            <div className="lg:hidden ml-auto">
              <button 
                className="text-gray-800 focus:outline-none" 
                onClick={toggleMenu}
              >
                <FontAwesomeIcon icon={faBars} size="lg" />
              </button>
            </div>
          </div>
          
          {/* Navigation Links Row */}
          <div className="hidden lg:flex justify-center items-center py-3">

            {/* Desktop Navigation Links - Centered */}
            <div className="flex items-center space-x-8">
              {navItems.map((item, index) => (
                <div
                  key={item.path}
                  className="transform transition-all duration-300"
                  style={{
                    transitionDelay: `${index * 50}ms`
                  }}
                >
                  <NavLink href={item.path}>
                    {language === 'en' ? item.name_EN : item.name_UR}
                  </NavLink>
                </div>
              ))}
              
              {/* Language Toggle Button with links */}
              <div className="transform transition-all duration-300">
                <button
                  className="bg-gradient-to-r from-yellow-300 to-yellow-400 text-gray-900 font-bold py-2 px-4 rounded-full shadow-lg text-sm transition-all duration-300 hover:scale-105 focus:outline-none shadow-yellow-300/50 hover:shadow-yellow-300/70"
                  onClick={toggleLanguage}
                >
                  {language === 'en' ? 'Translate to Urdu' : 'انگریزی میں ترجمہ کریں'}
                </button>
              </div>
            </div>
          </div>
          </div>
        </nav>
      </header>

      {/* Right-side drawer with close button inside */}
      <div
        className={`fixed top-0 right-0 h-full w-64 bg-white shadow-lg transform transition-transform duration-300 z-[60] ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Close button positioned absolutely within drawer */}
        <button 
          className="absolute top-5 right-5 text-gray-800 focus:outline-none" 
          onClick={toggleMenu}
        >
          <FontAwesomeIcon icon={faTimes} size="lg" />
        </button>

        <div className="flex flex-col p-6 space-y-4 mt-8">
          {/* Mobile menu title */}
          <div className="flex justify-center mb-4">
            <h2 className="text-lg font-bold text-blue-800">Menu</h2>
          </div>
          
          {/* Navigation links */}
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="text-lg font-medium text-gray-800 hover:text-blue-800 transition-colors duration-300"
              onClick={closeMenu}
            >
              {language === 'en' ? item.name_EN : item.name_UR}
            </Link>
          ))}
          <button
            className="flex items-center gap-2 bg-gradient-to-r from-yellow-300 to-yellow-400 text-gray-900 font-bold py-2 px-6 rounded-full shadow-lg text-lg transition-all duration-300 hover:scale-105 focus:outline-none animate-pulse shadow-yellow-300/50 hover:shadow-yellow-300/70"
            onClick={() => {
              toggleLanguage();
              closeMenu();
            }}
          >
            {language === 'en' ? 'Translate to Urdu' : 'انگریزی میں ترجمہ کریں'}
          </button>
        </div>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black opacity-50 z-50"
          onClick={toggleMenu}
        ></div>
      )}
    </>
  );
};

export default Navbar;